import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  jobTitle: string;
  department: string;
  initials: string;
  progress: number;
  requiredTraining: string;
  nextDeadline: string;
  overdue: number;
  phone?: string;
  location?: string;
  manager?: string;
  joinDate: string;
  lastLogin: string;
  completedCourses: number;
  inProgressCourses: number;
}

export interface Course {
  id: string;
  name: string;
  duration: string;
  lessons: number;
  icon: string;
}

export interface LearningHistoryItem {
  id: string;
  name: string;
  type: string;
  status: string;
  progress: number;
  score: string;
  enrolled: string;
  dueDate: string;
  overdue: number;
  completed: string;
  certificate: boolean;
}

export interface AdminActivity {
  action: string;
  date: string;
}

export interface TeamStats {
  totalMembers: number;
  activeMembers: string;
  activeLearners: number;
  activeLearnersStatus: string;
  completionRate: string;
  completionRateChange: string;
  complianceRate: string;
  complianceIssue: string;
}

export interface Team {
  id: number;
  name: string;
  description: string;
  createdAt: string;
  memberCount: number;
  /** Org-scoped fields (added when teams are fetched via the admin org-scoped endpoint). */
  organization_id?: number | null;
  organization_name?: string | null;
  manager_id?: string | null;
  manager_name?: string | null;
}

export interface AvailableUser {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email: string;
  department: string;
  jobTitle: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

// ===== API ENDPOINTS =====
const ENDPOINTS = {
  TEAMS: '/admin/teams',
  TEAM_STATS: '/admin/teams/stats',
  TEAM_MEMBERS: '/admin/teams/members',
  LEARNING_HISTORY: '/admin/teams/learning-history',
  AVAILABLE_USERS: '/admin/teams/available-users',
  AVAILABLE_COURSES: '/admin/teams/available-courses',
};

// ===== SERVICE FUNCTIONS =====

export const getTeamMembers = async (teamId?: number): Promise<{
  success: boolean;
  data?: TeamMember[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = teamId ? `?teamId=${teamId}` : '';
    const response = await httpClient.get<ApiResponse<TeamMember[]>>(
      `${ENDPOINTS.TEAM_MEMBERS}${queryParams}`
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error fetching team members:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch team members' }
    };
  }
};

export const getCourses = async (): Promise<{
  success: boolean;
  data?: Course[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Course[]>>(
      ENDPOINTS.AVAILABLE_COURSES
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error fetching courses:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch courses' }
    };
  }
};

export const getLearningHistory = async (
  teamId?: number,
  userId?: string,
  status?: string
): Promise<{
  success: boolean;
  data?: LearningHistoryItem[];
  error?: { message: string } | null;
}> => {
  try {
    const params = new URLSearchParams();
    if (teamId) params.append('teamId', teamId.toString());
    if (userId) params.append('userId', userId);
    if (status) params.append('status', status);

    const queryString = params.toString();
    const response = await httpClient.get<ApiResponse<LearningHistoryItem[]>>(
      `${ENDPOINTS.LEARNING_HISTORY}${queryString ? `?${queryString}` : ''}`
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error fetching learning history:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learning history' }
    };
  }
};

export const getAdminActivities = async (userId?: string): Promise<{
  success: boolean;
  data?: AdminActivity[];
  error?: { message: string } | null;
}> => {
  try {
    // Admin activities are derived from user data
    // For now, return static data - can be enhanced later
    const response: AdminActivity[] = [
      { action: 'Account created', date: 'N/A' },
      { action: 'Last login', date: 'N/A' }
    ];

    return {
      success: true,
      data: response,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch admin activities' }
    };
  }
};

export const getTeamStats = async (): Promise<{
  success: boolean;
  data?: TeamStats;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<TeamStats>>(
      ENDPOINTS.TEAM_STATS
    );

    return {
      success: true,
      data: response.data || null,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error fetching team stats:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch team stats' }
    };
  }
};

// ===== TEAM CRUD OPERATIONS =====

export const getAllTeams = async (): Promise<{
  success: boolean;
  data?: Team[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Team[]>>(
      ENDPOINTS.TEAMS
    );

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error fetching teams:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch teams' }
    };
  }
};

export const getTeamById = async (teamId: number): Promise<{
  success: boolean;
  data?: { team: Team; members: TeamMember[] };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<{ team: Team; members: TeamMember[] }>>(
      `${ENDPOINTS.TEAMS}/${teamId}`
    );

    return {
      success: true,
      data: response.data || null,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error fetching team:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch team' }
    };
  }
};

export const createTeam = async (
  name: string,
  description: string,
  organizationId?: number | null,
  managerId?: string | null
): Promise<{
  success: boolean;
  data?: { id: number };
  error?: { message: string } | null;
}> => {
  try {
    const body: any = { name, description };
    if (organizationId) body.organizationId = organizationId;
    if (managerId) body.managerId = managerId;

    const response = await httpClient.post<ApiResponse<{ id: number }>>(
      ENDPOINTS.TEAMS,
      body
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error creating team:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to create team' }
    };
  }
};

/**
 * Set or change a team's manager. Pass null to clear.
 */
export const setTeamManager = async (
  teamId: number,
  managerId: string | null
): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.patch<ApiResponse<void>>(
      `${ENDPOINTS.TEAMS}/${teamId}/manager`,
      { managerId }
    );
    return { success: true, error: null };
  } catch (error: any) {
    console.error('MyTeamService: Error setting team manager:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to set team manager' }
    };
  }
};

export const updateTeam = async (teamId: number, name: string, description: string): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.put<ApiResponse<void>>(
      `${ENDPOINTS.TEAMS}/${teamId}`,
      { name, description }
    );

    return {
      success: true,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error updating team:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to update team' }
    };
  }
};

export const deleteTeam = async (teamId: number): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.delete<ApiResponse<void>>(
      `${ENDPOINTS.TEAMS}/${teamId}`
    );

    return {
      success: true,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error deleting team:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to delete team' }
    };
  }
};

// ===== TEAM MEMBER OPERATIONS =====

export const getAvailableUsers = async (excludeTeamId?: number): Promise<{
  success: boolean;
  data?: AvailableUser[];
  error?: { message: string } | null;
}> => {
  try {
    const url = excludeTeamId
      ? `${ENDPOINTS.AVAILABLE_USERS}?excludeTeamId=${excludeTeamId}`
      : ENDPOINTS.AVAILABLE_USERS;
    const response = await httpClient.get<ApiResponse<AvailableUser[]>>(url);

    return {
      success: true,
      data: response.data || [],
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error fetching available users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch available users' }
    };
  }
};

export const addTeamMembers = async (teamId: number, userIds: string[]): Promise<{
  success: boolean;
  data?: { addedCount: number };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ addedCount: number }>>(
      `${ENDPOINTS.TEAMS}/${teamId}/members`,
      { userIds }
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error adding team members:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to add team members' }
    };
  }
};

export const removeTeamMember = async (teamId: number, userId: string): Promise<{
  success: boolean;
  error?: { message: string } | null;
}> => {
  try {
    await httpClient.delete<ApiResponse<void>>(
      `${ENDPOINTS.TEAMS}/${teamId}/members/${userId}`
    );

    return {
      success: true,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error removing team member:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to remove team member' }
    };
  }
};

// ===== COURSE ASSIGNMENT =====

export const assignCoursesToTeam = async (
  teamId: number,
  courses: Array<{ courseId: number; deadline?: string }>
): Promise<{
  success: boolean;
  data?: { assignedCourses: number; enrolledUsers: number };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ assignedCourses: number; enrolledUsers: number }>>(
      `${ENDPOINTS.TEAMS}/${teamId}/assign-courses`,
      { courses }
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error assigning courses:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to assign courses' }
    };
  }
};

export const bulkEnrollUsers = async (
  userIds: string[],
  courseIds: string[],
  deadline?: string
): Promise<{
  success: boolean;
  data?: { enrolledCount: number; userCount: number; courseCount: number };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ enrolledCount: number; userCount: number; courseCount: number }>>(
      `${ENDPOINTS.TEAMS}/bulk-enroll`,
      { userIds, courseIds, deadline }
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('MyTeamService: Error bulk enrolling users:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to enroll users in courses' }
    };
  }
};
